import React, { useMemo } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { polygonArea } from "../utils/area";
import { polygonPerimeter } from "../utils/perimeter";
import colors from "tailwindcss/colors";
import { SurfaceIcon } from "../components/Icons";
import { SiteMapSurfaceType, SurfaceType } from "../DataAndMap";

const FT_TO_ACRES_THRESHOLD = 43560; // 1 acre in square feet
const FT_TO_ACRES_CONVERSION = 43560; // 1 acre in square feet

const AllMeasurements = ({
  surfaceTypes,
  surfaceFeatures,
}: {
  surfaceTypes: SurfaceType[];
  surfaceFeatures: SiteMapSurfaceType[];
}) => {
  const geometry = useMapsLibrary("geometry");

  // Group surface features by surfaceType and calculate totals
  const surfaceTypeTotals = useMemo(() => {
    if (!geometry?.spherical) return {};

    const inFeet = true;

    const grouped = surfaceFeatures
      .filter((feature) => feature.properties.surfaceType !== "snowPile")
      .reduce(
        (acc, feature) => {
          const surfaceType = feature.properties.surfaceType;
          if (!acc[surfaceType]) {
            acc[surfaceType] = {
              features: [],
              totalArea: 0,
              totalPerimeter: 0,
              totalAreaLabel: inFeet ? "sq ft" : "sq m",
              totalPerimeterLabel: inFeet ? "ft" : "m",
            };
          }
          acc[surfaceType].features.push(feature);
          return acc;
        },
        {} as Record<
          string,
          {
            features: typeof surfaceFeatures;
            totalArea: number;
            totalPerimeter: number;
            totalAreaLabel: string;
            totalPerimeterLabel: string;
          }
        >,
      );

    // Calculate totals for each surface type
    Object.keys(grouped).forEach((surfaceType) => {
      const group = grouped[surfaceType];
      group.features.forEach((feature) => {
        if (feature.coordinates) {
          const area = polygonArea(
            geometry.spherical.computeArea,
            feature.coordinates,
            inFeet,
          );
          const perimeter = polygonPerimeter(
            geometry.spherical.computeLength,
            feature.coordinates,
            inFeet,
          );
          group.totalArea += area;
          group.totalPerimeter += perimeter;
        }
      });

      if (
        inFeet &&
        surfaceType === "parkingLot" &&
        group.totalArea > FT_TO_ACRES_THRESHOLD
      ) {
        group.totalAreaLabel = "acres";
        group.totalArea = parseFloat(
          (group.totalArea / FT_TO_ACRES_CONVERSION).toFixed(2),
        );
      }
    });

    return grouped;
  }, [surfaceFeatures, geometry]);

  // Get surface type labels
  const getSurfaceTypeLabel = (value: string) => {
    const surfaceType = surfaceTypes.find((type) => type.value === value);
    return surfaceType?.label || value;
  };

  return (
    <>
      <div className="space-y-4">
        {Object.entries(surfaceTypeTotals).map(
          ([surfaceType, totals], index) => {
            const color =
              surfaceTypes.find((type) => type.value === surfaceType)?.color ||
              colors.gray[500];
            const isLastEntry =
              index === Object.entries(surfaceTypeTotals).length - 1;
            const containerClassName = isLastEntry
              ? ""
              : "border-b border-gray-200 pb-4";

            return (
              <div key={surfaceType} className={containerClassName}>
                <h4 className="text-md mb-2">
                  {getSurfaceTypeLabel(surfaceType)}
                  {totals.features.length > 1 ? "s" : ""}
                  <span className="inline-block ml-2 w-4 h-4 pt-0.5">
                    <SurfaceIcon color={color} />
                  </span>
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Area</span>
                    <span className="font-medium">
                      {totals.totalArea.toLocaleString()}{" "}
                      {totals.totalAreaLabel}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Perimeter</span>
                    <span className="font-medium">
                      {totals.totalPerimeter.toLocaleString()}{" "}
                      {totals.totalPerimeterLabel}
                    </span>
                  </div>
                </div>
              </div>
            );
          },
        )}
      </div>
    </>
  );
};

export default AllMeasurements;
